'use client'

import { useState, useMemo } from 'react'

import { useFollowing } from '../../hooks/useFollowing'
import { FriendsSearch } from '../FriendsSearch'
import { ProfileSearchCard } from '../ProfileSearchCard'
import { UserModal } from '../UserProfile/Modal'

import type { ProfileCardRecord } from '@/features/profiles/types/profile-search'
import { RankingCardSkeleton } from '@/features/ranking'
import { useModalContext } from '@/shared/contexts/Modal'
import { useAppSearchParams } from '@/shared/lib/router'
import type { User } from '@/shared/types/user'
import { Card, CardBody, CardHeader, CardTitle } from '@/shared/ui/Card'
import { NotFoundAlert } from '@/shared/ui/NotFoundAlert'
import { Page } from '@/shared/ui/Page'
import { Pagination } from '@/shared/ui/Pagination'
import { Tabs, Tab } from '@/shared/ui/Tabs'

const FRIENDS_PER_PAGE = 10

function userToCardRecord(user: User): ProfileCardRecord {
  return {
    uid: user.uid,
    name: user.name,
    avatar: user.avatar,
    frame: user.frame,
    background: user.background,
    points: user.points
  }
}

type FriendsView = 'list' | 'search'

export function FriendsPage() {
  const searchParams = useAppSearchParams()
  const initialView: FriendsView =
    searchParams.get('tab') === 'search' ? 'search' : 'list'

  const [view, setView] = useState<FriendsView>(initialView)
  const [friendsPage, setFriendsPage] = useState(1)
  const { openModal } = useModalContext()
  const { records, isLoading, error, refetch } = useFollowing({
    enabled: view === 'list'
  })

  const friendsPageCount = Math.max(
    1,
    Math.ceil(records.length / FRIENDS_PER_PAGE)
  )
  const effectiveFriendsPage = Math.min(friendsPage, friendsPageCount)
  const paginatedFriends = useMemo(() => {
    const start = (effectiveFriendsPage - 1) * FRIENDS_PER_PAGE
    return records.slice(start, start + FRIENDS_PER_PAGE)
  }, [records, effectiveFriendsPage])
  const showFriendsPagination = records.length > FRIENDS_PER_PAGE

  const handleProfileClick = (record: { uid: string }) => {
    openModal({ modal: UserModal }, { userUid: record.uid })
  }

  return (
    <Page variant="yellow">
      <Card className="max-w-2xl w-full">
        <CardHeader>
          <CardTitle>Amigos</CardTitle>
        </CardHeader>
        <CardBody className="flex flex-col gap-4">
          <Tabs
            value={view}
            onValueChange={v => setView(v as FriendsView)}
            className="justify-center"
          >
            <Tab value="list">Lista</Tab>
            <Tab value="search">Buscar</Tab>
          </Tabs>

          {view === 'list' && (
            <>
              {isLoading && (
                <div
                  className="flex flex-col gap-2 py-2"
                  aria-busy="true"
                  aria-live="polite"
                >
                  <RankingCardSkeleton />
                  <RankingCardSkeleton />
                  <RankingCardSkeleton />
                </div>
              )}
              {!isLoading && error && (
                <div className="py-4 text-center text-sm text-destructive">
                  Erro ao carregar amigos. Tente novamente.
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="ml-2 underline font-medium"
                  >
                    Tentar novamente
                  </button>
                </div>
              )}
              {!isLoading && !error && records.length === 0 && (
                <div className="flex flex-col items-center gap-2 py-4">
                  <NotFoundAlert
                    title="Nenhum amigo"
                    text="Adicione amigos buscando por nome."
                  />
                  <button
                    type="button"
                    onClick={() => setView('search')}
                    className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                  >
                    Buscar
                  </button>
                </div>
              )}
              {!isLoading && !error && records.length > 0 && (
                <>
                  <ul
                    className="flex flex-col list-none p-0 m-0 overflow-auto max-h-[60vh] [&>li]:border-b-2 [&>li]:border-jacaranda/30 [&>li:last-child]:border-b-0"
                    aria-label="Lista de amigos"
                  >
                    {paginatedFriends.map(user => (
                      <li key={user.uid}>
                        <ProfileSearchCard
                          record={userToCardRecord(user)}
                          onClick={() => handleProfileClick({ uid: user.uid })}
                        />
                      </li>
                    ))}
                  </ul>
                  {showFriendsPagination && (
                    <Pagination
                      currentPage={effectiveFriendsPage}
                      pageCount={friendsPageCount}
                      totalCount={records.length}
                      onPrev={() => setFriendsPage(p => Math.max(1, p - 1))}
                      onNext={() =>
                        setFriendsPage(p => Math.min(friendsPageCount, p + 1))
                      }
                      ariaLabel="Paginação da lista de amigos"
                    />
                  )}
                </>
              )}
            </>
          )}

          {view === 'search' && (
            <FriendsSearch onProfileClick={handleProfileClick} />
          )}
        </CardBody>
      </Card>
    </Page>
  )
}
